import { Component, forwardRef, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { InputBaseComponent } from '../input-base/input-base.component';
import { Validators } from '@angular/forms';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'input-checkbox',
  templateUrl: './input-checkbox.component.html',
  styleUrls: ['./input-checkbox.component.scss'],
  providers: [{ provide: InputBaseComponent, useExisting: forwardRef(() => InputCheckboxComponent) }],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class InputCheckboxComponent extends InputBaseComponent implements OnInit, OnDestroy {

  @Input() public labelAfterCheckbox: boolean = true;                             // not implemented - kept for interface-compatibility
  @Input() public cbCheckedValue: any = true;
  @Input() public cbUncheckedValue: any = false;
  @Input() public initialChecked: boolean = false;
  @Input() public setRequiredTrueOnInit: boolean = false;                         // sets reqTrue.Validator in OnInit
  @Input() public unsetRequiredTrueOnDestroy: boolean = false;                    // unsets reqTrue.Validator in OnDestroy
  @Output() public checked: EventEmitter<any> = new EventEmitter<any>();
  @Output() public unchecked: EventEmitter<any> = new EventEmitter<any>();

  constructor() {
    super();
  }

  /**
   * True when the control carries required or requiredTrue. Read straight off the
   * control rather than from controlIsRequired$, which only tracks Validators.required
   * and is fed on a setTimeout - a checkbox is usually made mandatory with requiredTrue.
   */
  public get isRequired(): boolean {
    return this.control.hasValidator(Validators.required) || this.control.hasValidator(Validators.requiredTrue);
  }

  /**
   * Toggles the control from the native checkbox' change-event. Not bound with
   * [formControl], because cbCheckedValue / cbUncheckedValue need not be booleans.
   *
   * The native event stops here. Angular binds a host's (change) to the DOM event of
   * that name as well as to the output, so a bubbling event ran every (change)
   * handler twice - first with the Event, which reads as "checked", then with the
   * value (#6259).
   */
  public toggle(event: Event): void {
    event.stopPropagation();
    if (this.readonly || this.control.disabled) {
      // pointer-events:none blocks the mouse on a readonly checkbox, but not Space on
      // a focused one. The [checked]-binding does not change, so Angular re-renders
      // nothing and the DOM would stay out of sync with the control - reset it here.
      (event.target as HTMLInputElement).checked = this.control.value === this.cbCheckedValue;
      return;
    }
    this.control.setValue(this.control.value === this.cbCheckedValue ? this.cbUncheckedValue : this.cbCheckedValue);
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.change.subscribe((val) => {
      if (val === this.cbCheckedValue) {
        this.checked.emit(this.cbCheckedValue);
      }
      else if (val === this.cbUncheckedValue) {
        this.unchecked.emit(this.cbUncheckedValue);
      }
    });
    if (this.initialChecked) {
      this.control.setValue(this.cbCheckedValue);
    }
    if (this.setRequiredTrueOnInit) {
      this.setRequiredTrueState(true);
    }
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.unsetRequiredTrueOnDestroy) {
      this.setRequiredTrueState(false);
    }
  }

  public setRequiredTrueState(required: boolean): void {
    if (required && !this.control.hasValidator(Validators.requiredTrue)) {
      this.control.addValidators(Validators.requiredTrue);
    }
    else if (!required && this.control.hasValidator(Validators.requiredTrue)) {
      this.control.removeValidators(Validators.requiredTrue);
    }
    this.control.updateValueAndValidity();
  }

}
